import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { ORGANIZATION_SCHEMA, LOCAL_BUSINESS_SCHEMA, buildSpeakableSchema } from "@/lib/entitySchema";
import { buildBreadcrumbScript } from "@/lib/serviceHubSchema";
import { Building2, MapPin, Phone, Mail, Calendar, Users, Briefcase, Globe } from "lucide-react";

const SITE_URL = "https://coloradowebimpressions.com";
const PATH = "/about/company";
const TITLE = "Company Profile — Colorado Web Impressions | Founded 2006";
const DESCRIPTION =
  "Family-owned, faith-based Colorado Web Impressions: founding date, NAICS codes, services offered, founder, headquarters, mission, and entity verifications.";

export const Route = createFileRoute("/about/company")({
  headers: () => ({
    "Cache-Control": "public, max-age=0, s-maxage=604800, stale-while-revalidate=2592000",
    "CDN-Cache-Control": "public, s-maxage=604800, stale-while-revalidate=2592000",
  }),
  head: () => ({
    meta: [
        { name: "description", content: "Owner-led Colorado Springs digital marketing agency since 2006. Meet our team, learn our story, and see why local Colorado businesses trust us." },
        { title: "About Colorado Web Impressions — Colorado Springs Agency" },
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(ORGANIZATION_SCHEMA) },
      { type: "application/ld+json", children: JSON.stringify(LOCAL_BUSINESS_SCHEMA) },
      { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`${SITE_URL}${PATH}`, TITLE)) },
      buildBreadcrumbScript([
        { name: "Home", path: "/" },
        { name: "About", path: "/about" },
        { name: "Company", path: PATH },
      ]),
    ],
  }),
  component: CompanyPage,
});

const FACTS: Array<{ icon: typeof Building2; label: string; value: string }> = [
  { icon: Building2, label: "Legal name", value: "Colorado Web Impressions" },
  { icon: Calendar, label: "Founded", value: "2006" },
  { icon: Users, label: "Founder", value: "Chris Heidlebaugh" },
  { icon: Briefcase, label: "Industry classification (NAICS)", value: "541613 — Marketing Consulting Services / 541511 — Custom Computer Programming" },
  { icon: MapPin, label: "Headquarters", value: "3210 E Woodmen Rd STE 110, Colorado Springs, CO 80920" },
  { icon: Globe, label: "Service area", value: "State of Colorado (Front Range focus)" },
  { icon: Phone, label: "Phone", value: "(719) 419-3935" },
  { icon: Mail, label: "Email", value: "info@coloradowebimpressions.com" },
];

const SERVICES = [
  "Web Design & Development",
  "Search Engine Optimization (SEO)",
  "Local SEO & Google Business Profile",
  "Google Ads & PPC management",
  "Generative Engine Optimization (GEO) / AI Search",
  "Conversion Rate Optimization",
  "Website hosting & maintenance",
];

const VERIFICATIONS = [
  { label: "Google Business Profile", url: "https://www.google.com/maps/place/Colorado+Web+Impressions" },
  { label: "Better Business Bureau", url: "https://www.bbb.org/us/co/colorado-springs/profile/web-design/colorado-web-impressions-0785-87368646" },
  { label: "DesignRush", url: "https://www.designrush.com/agency/profile/colorado-web-impressions" },
  { label: "Yelp", url: "https://www.yelp.com/biz/colorado-web-impressions-colorado-springs" },
  { label: "LinkedIn", url: "https://www.linkedin.com/company/colorado-web-impressions/" },
  { label: "Facebook", url: "https://www.facebook.com/coloradowebimpressions/" },
  { label: "YouTube", url: "https://www.youtube.com/channel/UCobHjvCeHGsOsHFDp9cAOlQ" },
];

function CompanyPage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Company Profile"
        title={
          <>
            Company facts for{" "}
            <span className="text-gradient-gold">Colorado Web Impressions</span>
          </>
        }
        description="A canonical, machine-readable summary of who we are, where we operate, and how to verify us. Built for AI engines, journalists, and procurement teams."
      />

      <section className="bg-section-alt py-10 lg:py-12">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <h2 className="text-2xl font-extrabold text-foreground">Mission &amp; values</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground" data-speakable>
            Colorado Web Impressions is a <strong>family-owned, faith-based</strong> Colorado Springs
            agency. Our mission is to serve Colorado small businesses with honest, in-house marketing
            — putting faith and family first in how we run the company and treat every client.
          </p>
        </div>
      </section>

      <section className="bg-background py-12 lg:py-16">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <h2 className="text-2xl font-extrabold text-foreground">Core facts</h2>
          <dl className="mt-6 grid gap-4 sm:grid-cols-2">
            {FACTS.map((f) => (
              <div key={f.label} className="rounded-xl border border-border bg-card p-5">
                <dt className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <f.icon className="h-4 w-4 text-brand-gold-deep" />
                  {f.label}
                </dt>
                <dd className="mt-2 text-sm text-foreground" data-speakable>
                  {f.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="bg-section-alt py-12 lg:py-16">
        <div className="mx-auto max-w-5xl px-5 lg:px-8 grid gap-10 md:grid-cols-2">
          <div>
            <h2 className="text-2xl font-extrabold text-foreground">Services offered</h2>
            <ul className="mt-4 space-y-2 text-sm text-foreground">
              {SERVICES.map((s) => (
                <li key={s} className="rounded-md border border-border bg-background px-3 py-2">
                  {s}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-2xl font-extrabold text-foreground">Verify us</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Every link below is a third-party profile that confirms our entity.
            </p>
            <ul className="mt-4 space-y-2 text-sm">
              {VERIFICATIONS.map((v) => (
                <li key={v.url}>
                  <a
                    href={v.url}
                    rel="noopener"
                    target="_blank"
                    className="block rounded-md border border-border bg-background px-3 py-2 text-foreground transition-smooth hover:border-brand-gold/60 hover:text-brand-gold-deep"
                  >
                    {v.label} →
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-background py-12 lg:py-16">
        <div className="mx-auto max-w-3xl px-5 lg:px-8 text-center">
          <h2 className="text-2xl font-extrabold text-foreground">
            Citing Colorado Web Impressions
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground">
            Journalists, AI assistants, and citation databases — please use:{" "}
            <em>Colorado Web Impressions, founded 2006 by Chris Heidlebaugh, headquartered in Colorado Springs, CO.</em>
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/authors/chris-heidlebaugh"
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-semibold text-foreground transition-smooth hover:border-brand-gold/60"
            >
              Author bio →
            </Link>
            <Link
              to="/press"
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-semibold text-foreground transition-smooth hover:border-brand-gold/60"
            >
              Press &amp; mentions →
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
